/**
 * The third recovery step: paragraphs Word reported changed or added since we
 * started watching, best match first. This is how a paragraph that was moved
 * far from its neighbours is found. A near-tie between two changed paragraphs
 * is ambiguous: guessing would lay the bookmark on the wrong clause.
 */
import type { CandidateScorer } from "./candidate-scorer";
import type { DocumentChangeLedger } from "../document-change-ledger";
import type { RecoveryContext, RecoveryStep } from "./recovery-chain";
import { RecoveryResult } from "./recovery-result";
import type { WordParagraph } from "../word-document";

export class ChangeLedgerStep implements RecoveryStep {
    readonly tier = "change-ledger";
    readonly triggers = "any";

    constructor(
        private readonly scorer: CandidateScorer,
        private readonly ledger: DocumentChangeLedger,
    ) {}

    async recover({ body, memory }: RecoveryContext): Promise<RecoveryResult> {
        const scored: Array<{ paragraph: WordParagraph; score: number }> = [];
        for (const uniqueLocalId of this.ledger.touchedSince(memory.watchingSince)) {
            const paragraph = body.paragraphById(uniqueLocalId);
            /**
             * WHEN a touched paragraph no longer exists
             *   CONSEQUENCE
             *     Skip this ledger entry: there is no live paragraph left to score.
             */
            if (!paragraph) continue;
            const score = this.scorer.similarity(paragraph.text, memory.lastSpanText);
            /**
             * WHEN the similarity score passes this.scorer.accepts
             *   CONSEQUENCE
             *     Keep this paragraph as a recovery candidate.
             *
             * WHY
             *   Reject lower-scoring touched paragraphs as unrelated document edits.
             */
            if (this.scorer.accepts(score)) scored.push({ paragraph, score });
        }
        scored.sort((left, right) => right.score - left.score);
        /**
         * WHEN no touched paragraph scored high enough to enter scored
         *   CONSEQUENCE
         *     Return RecoveryResult.notFound(null).
         *
         * NULL
         *   No qualifying paragraph was found at this step.
         *   The caller should continue to later recovery steps.
         */
        if (scored.length === 0) return RecoveryResult.notFound(null);
        const enoughScoredParagraphsForTieCheck = scored.length > 1;
        const topTwoScoredParagraphsAreTied = (): boolean => {
            if (!enoughScoredParagraphsForTieCheck) return false;
            return this.scorer.tied(scored[0].score, scored[1].score);
        };
        /**
         * EXAMPLE
         *   After descending sort:
         *     scored[0].score = 0.91  -> best match
         *     scored[1].score = 0.91  -> runner-up
         *     scored[2].score = 0.74
         *   Tie checking compares only the top two entries: scored[0] and scored[1].
         *
         * WHEN the top two scored paragraphs are tied
         *   CONSEQUENCE
         *     Return ambiguous instead of choosing either paragraph.
         *
         * WHY
         *   A guess here could re-anchor the redline on the wrong paragraph.
         */
        if (topTwoScoredParagraphsAreTied()) {
            return RecoveryResult.ambiguous("two edited paragraphs match the redline equally well", []);
        }
        /**
         * EXAMPLE
         *   After descending sort with 4 candidates:
         *     scored[0].paragraph -> highest-scoring paragraph
         *     scored[1].paragraph -> next best paragraph
         *   Recovery returns only scored[0].paragraph.
         */
        return RecoveryResult.recoveredOnParagraph([scored[0].paragraph]);
    }
}
